/* =====================================================================
   BURASHID — motion & interactions for the Dawn customization
   Every feature is opt-in through data-attributes rendered by br-* sections,
   and re-initialises inside the theme editor (shopify:section:load).
   ===================================================================== */
(() => {
  const html = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const isRTL = () => (html.getAttribute('dir') || 'ltr') === 'rtl';

  /* ---------- Reveal on scroll ---------- */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
      $$('[data-br-count]', e.target).forEach(countUp);
      if (e.target.matches('[data-br-count]')) countUp(e.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }) : null;

  function initReveal(root) {
    $$('[data-br-stagger]', root).forEach((wrap) => {
      [...wrap.children].forEach((c, i) => { if (!c.style.getPropertyValue('--i')) c.style.setProperty('--i', i % (+wrap.dataset.brStagger || 8)); });
    });
    $$('[data-br-r]', root).forEach((el) => {
      if (el.dataset.brSeen) return; el.dataset.brSeen = 1;
      if (!html.classList.contains('br-motion') || !io || reduce) { el.classList.add('is-in'); return; }
      io.observe(el);
    });
  }

  /* ---------- Counters ---------- */
  function countUp(el) {
    if (el.dataset.done) return; el.dataset.done = 1;
    const to = parseFloat(el.dataset.brCount), pre = el.dataset.prefix || '', suf = el.dataset.suffix || '';
    const from = el.dataset.from ? parseFloat(el.dataset.from) : 0;
    if (reduce || !html.classList.contains('br-motion')) { el.textContent = pre + to + suf; return; }
    const t0 = performance.now(), d = 1800;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / d), e = 1 - Math.pow(1 - k, 4);
      el.textContent = pre + Math.round(from + (to - from) * e) + suf;
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* ---------- Carousel ---------- */
  function initCarousel(car) {
    if (car.dataset.brReady) return; car.dataset.brReady = 1;
    const tr = $('.br-car__track', car); if (!tr) return;
    const bar = $('.br-car__prog i', car), prev = $('.br-car__btn--prev', car), next = $('.br-car__btn--next', car);
    const gap = () => parseFloat(getComputedStyle(tr).columnGap) || 0;
    const stepW = () => (tr.firstElementChild ? tr.firstElementChild.getBoundingClientRect().width + gap() : tr.clientWidth);
    const dir = () => (isRTL() ? -1 : 1);
    const state = () => {
      const max = tr.scrollWidth - tr.clientWidth, pos = Math.abs(tr.scrollLeft), ratio = tr.clientWidth / Math.max(1, tr.scrollWidth);
      if (bar) { bar.style.width = Math.min(100, ratio * 100) + '%'; bar.style.transform = `translateX(${(isRTL() ? -1 : 1) * (max > 0 ? pos / max : 0) * (1 / ratio - 1) * 100}%)`; }
      if (prev) prev.disabled = pos < 4;
      if (next) next.disabled = pos > max - 4;
    };
    const go = (n) => tr.scrollBy({ left: n * stepW() * dir(), behavior: 'smooth' });
    prev && prev.addEventListener('click', () => go(-1));
    next && next.addEventListener('click', () => go(1));
    tr.addEventListener('scroll', state, { passive: true });
    addEventListener('resize', state);
    requestAnimationFrame(state);
    // mouse drag
    let down = false, sx = 0, sl = 0, moved = false;
    tr.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') return; down = true; moved = false; sx = e.clientX; sl = tr.scrollLeft; });
    addEventListener('pointermove', (e) => { if (!down) return; const d = e.clientX - sx; if (Math.abs(d) > 6) { moved = true; tr.classList.add('is-dragging'); } tr.scrollLeft = sl - d; });
    addEventListener('pointerup', () => { if (!down) return; down = false; tr.classList.remove('is-dragging'); });
    tr.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    // autoplay
    const ap = parseFloat(car.dataset.autoplay || 0);
    if (ap > 0 && !reduce) {
      let paused = false;
      if (car.dataset.pauseHover !== 'false') { car.addEventListener('mouseenter', () => (paused = true)); car.addEventListener('mouseleave', () => (paused = false)); }
      tr.addEventListener('touchstart', () => (paused = true), { passive: true });
      tr.addEventListener('touchend', () => setTimeout(() => (paused = false), 4000), { passive: true });
      const t = setInterval(() => {
        if (!document.body.contains(tr)) return clearInterval(t);
        if (paused || document.hidden || tr.offsetParent === null) return;
        const max = tr.scrollWidth - tr.clientWidth;
        if (Math.abs(tr.scrollLeft) >= max - 4) tr.scrollTo({ left: 0, behavior: 'smooth' });
        else go(1);
      }, ap * 1000);
    }
  }

  /* ---------- Tabs ---------- */
  function initTabs(wrap) {
    if (wrap.dataset.brReady) return; wrap.dataset.brReady = 1;
    const btns = $$('[data-br-tab]', wrap), panels = $$('[data-br-panel]', wrap), ind = $('.br-tabs__ind', wrap);
    const extra = $$('[data-br-tab-link]', wrap);
    const move = () => {
      const b = btns.find((x) => x.classList.contains('is-active')); if (!b || !ind) return;
      const lw = parseFloat(wrap.dataset.lineWidth || 100) / 100;
      ind.style.width = b.offsetWidth * lw + 'px';
      ind.style.left = b.offsetLeft + (b.offsetWidth * (1 - lw)) / 2 + 'px';
    };
    const show = (i, focus) => {
      btns.forEach((b, k) => { b.classList.toggle('is-active', k === i); b.setAttribute('aria-selected', k === i); b.tabIndex = k === i ? 0 : -1; });
      panels.forEach((p, k) => {
        const on = k === i; p.hidden = !on;
        if (on) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); $$('[data-br-car]', p).forEach(initCarousel); $$('[data-br-r]', p).forEach((el) => el.classList.add('is-in')); }
      });
      extra.forEach((a) => { const href = btns[i] && btns[i].dataset.link; if (href) a.href = href; });
      move();
      if (focus) btns[i].focus();
    };
    btns.forEach((b, i) => {
      b.addEventListener('click', () => show(i));
      b.addEventListener('keydown', (e) => {
        const n = btns.length; let k = null;
        if (e.key === 'ArrowRight') k = (i + (isRTL() ? -1 : 1) + n) % n;
        if (e.key === 'ArrowLeft') k = (i + (isRTL() ? 1 : -1) + n) % n;
        if (k !== null) { e.preventDefault(); show(k, true); }
      });
    });
    wrap.brShow = show;
    addEventListener('resize', move);
    requestAnimationFrame(move);
    document.fonts && document.fonts.ready.then(move);
  }

  /* ---------- Slideshow ---------- */
  function initSlideshow(ss) {
    if (ss.dataset.brReady) return; ss.dataset.brReady = 1;
    const slides = $$('.br-slide', ss), dots = $$('.br-ss__dot', ss);
    if (slides.length < 2) { slides[0] && slides[0].classList.add('is-active'); return; }
    let cur = 0, timer;
    const ms = parseFloat(ss.dataset.autoplay || 0) * 1000;
    const go = (n) => {
      slides[cur].classList.remove('is-active'); dots[cur] && dots[cur].classList.remove('is-active');
      cur = (n + slides.length) % slides.length;
      slides[cur].classList.add('is-active'); dots[cur] && dots[cur].classList.add('is-active');
      if (dots[cur]) { dots[cur].style.setProperty('--dot-dur', (ms || 6000) + 'ms'); }
      restart();
    };
    const restart = () => { clearTimeout(timer); if (ms > 0 && !reduce) timer = setTimeout(() => go(cur + 1), ms); };
    dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
    $('.br-ss__arrow--prev', ss) && $('.br-ss__arrow--prev', ss).addEventListener('click', () => go(cur - 1));
    $('.br-ss__arrow--next', ss) && $('.br-ss__arrow--next', ss).addEventListener('click', () => go(cur + 1));
    let sx = null;
    ss.addEventListener('touchstart', (e) => (sx = e.touches[0].clientX), { passive: true });
    ss.addEventListener('touchend', (e) => { if (sx === null) return; const d = e.changedTouches[0].clientX - sx; if (Math.abs(d) > 40) go(cur + ((d < 0) !== isRTL() ? 1 : -1)); sx = null; });
    ss.addEventListener('mouseenter', () => clearTimeout(timer));
    ss.addEventListener('mouseleave', restart);
    ss.brGo = go;
    slides[0].classList.add('is-active'); dots[0] && dots[0].classList.add('is-active');
    restart();
  }

  /* ---------- Sparks ---------- */
  function initSparks(el) {
    if (el.dataset.brReady || reduce) return; el.dataset.brReady = 1;
    const n = +el.dataset.brSparks || 20;
    el.innerHTML = Array.from({ length: n }, () => `<i style="left:${Math.random() * 100}%;animation-duration:${8 + Math.random() * 10}s;animation-delay:${-Math.random() * 18}s;transform:scale(${0.5 + Math.random()})"></i>`).join('');
  }

  /* ---------- Parallax ---------- */
  const px = [];
  function initParallax(el) { if (!reduce && !el.dataset.brReady) { el.dataset.brReady = 1; px.push(el); } }
  function runParallax() {
    const vh = innerHeight;
    px.forEach((el) => {
      if (!document.body.contains(el)) return;
      const r = el.parentElement.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      const f = parseFloat(el.dataset.brParallax || 0.08);
      el.style.transform = `translate3d(0, ${(r.top + r.height / 2 - vh / 2) * -f}px, 0) scale(1.08)`;
    });
  }

  /* ---------- Quick add (uses Dawn's cart drawer) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-br-add]');
    if (!btn) return;
    e.preventDefault();
    const drawer = document.querySelector('cart-drawer');
    btn.classList.add('is-loading');
    try {
      const body = { items: [{ id: +btn.dataset.brAdd, quantity: 1 }] };
      if (drawer && drawer.getSectionsToRender) {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status) { toast(data.description || data.message); return; }
      if (drawer && drawer.renderContents) {
        const item = data.items ? { ...data.items[0], sections: data.sections, id: data.items[0].id } : data;
        drawer.classList.contains('is-empty') && drawer.classList.remove('is-empty');
        drawer.renderContents(item);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
    } catch (err) {
      window.location.href = btn.dataset.url || '/cart';
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------- Sticky add-to-cart (mirrors Dawn's main product form) ---------- */
  function initSatc(bar) {
    if (bar.dataset.brReady) return; bar.dataset.brReady = 1;
    const main = document.querySelector('product-form .product-form__submit');
    const priceSrc = document.querySelector('.product__info-container .price');
    const priceDst = $('[data-satc-price]', bar);
    const btn = $('[data-satc-btn]', bar);
    if (!main) { bar.remove(); return; }
    document.body.classList.add('br-has-satc');
    const sync = () => {
      if (priceSrc && priceDst) {
        const sale = priceSrc.querySelector('.price__sale .price-item--sale');
        const reg = priceSrc.querySelector('.price__regular .price-item--regular');
        const onSale = priceSrc.classList.contains('price--on-sale');
        priceDst.textContent = ((onSale ? sale : reg) || reg || sale || {}).textContent || '';
      }
      btn.disabled = main.disabled;
      btn.textContent = (main.querySelector('span') || main).textContent.trim();
    };
    sync();
    if (priceSrc) new MutationObserver(sync).observe(priceSrc, { subtree: true, childList: true, characterData: true, attributes: true });
    new MutationObserver(sync).observe(main, { subtree: true, childList: true, attributes: true });
    btn.addEventListener('click', () => {
      if (bar.dataset.action === 'scroll') { main.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      main.click();
    });
    new IntersectionObserver(([e]) => bar.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0)).observe(main);
  }

  /* ---------- Back to top + header offset var ---------- */
  function initTotop() {
    const b = $('.br-totop'); if (!b || b.dataset.brReady) return; b.dataset.brReady = 1;
    const ring = $('.br-totop__ring circle', b);
    b.addEventListener('click', () => scrollTo({ top: 0, behavior: 'smooth' }));
    const on = () => {
      const y = scrollY, max = Math.max(1, document.documentElement.scrollHeight - innerHeight);
      b.classList.toggle('is-on', y > 700);
      if (ring) ring.style.strokeDashoffset = 138 - 138 * (y / max);
    };
    addEventListener('scroll', on, { passive: true }); on();
  }

  /* ---------- Toast ---------- */
  let tT;
  function toast(msg) {
    let t = $('.br-toast');
    if (!t) { t = document.createElement('div'); t.className = 'br-toast'; t.setAttribute('role', 'status'); document.body.appendChild(t); }
    t.textContent = msg; t.classList.add('is-on'); clearTimeout(tT); tT = setTimeout(() => t.classList.remove('is-on'), 2600);
  }
  window.brToast = toast;

  /* ---------- Init ---------- */
  function init(root = document) {
    initReveal(root);
    $$('[data-br-car]', root).forEach(initCarousel);
    $$('[data-br-tabs]', root).forEach(initTabs);
    $$('[data-br-ss]', root).forEach(initSlideshow);
    $$('[data-br-sparks]', root).forEach(initSparks);
    $$('[data-br-parallax]', root).forEach(initParallax);
    $$('[data-br-satc]', root).forEach(initSatc);
    $$('[data-br-count]', root).forEach((el) => { if (!html.classList.contains('br-motion')) countUp(el); });
    initTotop();
  }
  window.brInit = init;
  addEventListener('scroll', () => requestAnimationFrame(runParallax), { passive: true });
  const watch = () => {
    let pend = new Set(), raf = 0;
    new MutationObserver((ms) => {
      ms.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1 && n.parentElement) pend.add(n.parentElement); }));
      if (!pend.size || raf) return;
      raf = requestAnimationFrame(() => { raf = 0; const list = [...pend]; pend = new Set(); list.forEach((r) => document.contains(r) && init(r)); });
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { init(); watch(); });
  else { init(); watch(); }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const blk = e.target;
    const tabs = blk.closest('[data-br-tabs]');
    if (tabs && tabs.brShow && blk.dataset.brTab !== undefined) tabs.brShow(+blk.dataset.index || 0);
    const ss = blk.closest('[data-br-ss]');
    if (ss && ss.brGo && blk.classList.contains('br-slide')) ss.brGo(+blk.dataset.index || 0);
    $$('[data-br-r]', blk).forEach((el) => el.classList.add('is-in'));
  });
})();
